"use client";

import { useEffect, useState } from "react";

import { AiLoader } from "@/shared/ui/ai-loader";
import { cn } from "@/shared/lib/utils/cn";

type PendingState = { label: string } | null;

let pending: PendingState = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

/** Show a content-area loader immediately (before router.push / async save). */
export function beginRoutePending(label: string) {
  pending = { label: label.trim() || "Loading…" };
  emit();
}

/** Clear the overlay once the destination has painted. */
export function endRoutePending() {
  if (!pending) return;
  pending = null;
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getPending() {
  return pending;
}

/** Full-page centered loader — reuse on route shells and Suspense fallbacks. */
export function PageLoader({ label, className }: { label: string; className?: string }) {
  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col items-center justify-center bg-[var(--page)]/90 backdrop-blur-[2px]",
        className,
      )}
    >
      <AiLoader size="lg" label={label} />
    </div>
  );
}

/** Overlay for in-flight client navigations — covers the main content pane only. */
export function RoutePendingOverlay() {
  const [state, setState] = useState<PendingState>(getPending);

  useEffect(() => subscribe(() => setState(getPending())), []);

  if (!state) return null;

  return (
    <div className="absolute inset-0 z-[100] flex items-center justify-center bg-[var(--page)]/85 backdrop-blur-[3px]">
      <AiLoader size="lg" label={state.label} />
    </div>
  );
}

/** Call from destination pages so the departing overlay does not linger. */
export function useClearRoutePendingOnMount() {
  useEffect(() => {
    endRoutePending();
  }, []);
}
